/* eslint-disable no-constant-condition */
import React, { useEffect, useContext } from 'react'
import { useLazyQuery, NetworkStatus } from '@apollo/client'
import { Card, CardContent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@material-ui/core'
import { makeStyles, withStyles, createStyles, Theme } from '@material-ui/core/styles'
import moment from 'moment'
import { DailyMonitoringDetailsFiltersContext } from '../../../../context/DailyMonitoringDetailsFiltersContext'
import { GET_SUMARY_DATA, DailyMonitoring } from '../../../../queries/dailyMonitoring'
import TableSkeleton from '../../../../components/skeletons/TableSkeleton'
import { formatNumber, formatCurrency } from '../../../../utils/Helpers'

import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward'
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      marginTop: theme.spacing(2)
    },
    rowHoverFixed: {
      '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover
      }
    },
    centeredText: {
      textAlign: 'center'
    },
    downArrow: {
      fontSize: 12,
      color: '#EF6167'
    },
    upArrow: {
      fontSize: 12,
      color: '#06BAB2'
    }
  })
)

const StyledTableCell = withStyles((theme: Theme) =>
  createStyles({
    head: {
      backgroundColor: '#2D4054',
      color: theme.palette.common.white
    },
    body: {
      height: 50,
      textAlign: 'center',
      '&.blue': {
        backgroundColor: '#9ED6EE'
      },
      '&.grey': {
        backgroundColor: '#B6B6B6',
        color: theme.palette.common.black
      },
      '&.neutral': {
        color: '#000'
      },
      '&.negative': {
        backgroundColor: '#FFDCDE',
        color: '#EF6167'
      },
      '&.positive': {
        backgroundColor: '#c7ebdb',
        color: '#099461'
      },
      '&.bold': {
        fontWeight: 'bold'
      },
      '&.bordered': {
        borderBottom: '1px solid #4B4B4B'
      },
      '&:first-child': {
        textAlign: 'left'
      }
    }
  })
)(TableCell)

type Props = {
  children: string
  className?: string
}

const CellPrimary = ({ children, className }: Props): JSX.Element => {
  return <StyledTableCell className={`${className} ${'neutral'}`}>{children}</StyledTableCell>
}

const CellNumber = ({ children, className }: Props): JSX.Element => {
  const classes = useStyles()
  const isNegative = Math.sign(parseFloat(children)) === -1 ? true : children.indexOf('-') >= 0
  return <StyledTableCell className={`${className} ${isNegative ? 'negative' : 'positive'}`}>{children} {isNegative ? <ArrowDownwardIcon className={classes.downArrow} /> : <ArrowUpwardIcon className={classes.upArrow}/>}</StyledTableCell>
}

export default function MainSummary (): JSX.Element {
  const classes = useStyles()
  const { filters } = useContext(DailyMonitoringDetailsFiltersContext)
  const [getData, { loading, error, data, networkStatus }] = useLazyQuery(
    GET_SUMARY_DATA, {
      notifyOnNetworkStatusChange: true
    }
  )

  useEffect(() => {
    getData({
      variables: { filters }
    })
  }, [filters])

  if (networkStatus === NetworkStatus.refetch) return <TableSkeleton />
  if (loading || !data) return <TableSkeleton />
  if (error && !data) return <p>Error :(</p>
  if (!data.dailyMonitoring) return <p>No data found</p>

  const {
    goalMonth,
    goalPartial,
    fulfilledInMonth,
    goalDiff,
    goalDiffPercent,
    accomplishedData
  }: DailyMonitoring = data.dailyMonitoring

  return (
    <Card className={classes.root}>
      <CardContent>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <StyledTableCell>{moment(filters?.date?.month, 'MM').format('MMM')}</StyledTableCell>
                <StyledTableCell className={classes.centeredText}>META Mês</StyledTableCell>
                <StyledTableCell className={classes.centeredText}>META Parcial</StyledTableCell>
                <StyledTableCell className={classes.centeredText}>Realizado mês</StyledTableCell>
                <StyledTableCell className={classes.centeredText}>Desvio meta</StyledTableCell>
                <StyledTableCell className={classes.centeredText}>% Var Desvio</StyledTableCell>
                <StyledTableCell className={classes.centeredText}>{moment(accomplishedData?.month, 'MM').format('MMM')}/{accomplishedData?.year - 1}</StyledTableCell>
                <StyledTableCell className={classes.centeredText}>Projeção {moment(accomplishedData?.month, 'MM').format('MMM')}/{`${accomplishedData?.year}`}</StyledTableCell>
                <StyledTableCell className={classes.centeredText}>Var% Ano Ant.</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow className={classes.rowHoverFixed}>
                <StyledTableCell className="bold bordered">R$ GMV</StyledTableCell>
                <StyledTableCell className="blue bold bordered">{formatCurrency(goalMonth.ordersTotal, 2)}</StyledTableCell>
                <StyledTableCell className="blue bold bordered">{formatCurrency(goalPartial.ordersTotal, 2)}</StyledTableCell>
                <CellPrimary className="bordered">{formatCurrency(fulfilledInMonth.ordersTotal, 2)}</CellPrimary>
                <CellNumber className="bordered">{formatCurrency(goalDiff.ordersTotal, 2)}</CellNumber>
                <CellNumber className="bordered">{`${formatNumber(goalDiffPercent.ordersTotal, 1)}%`}</CellNumber>
                <CellPrimary className="bordered">{`${formatCurrency(accomplishedData.yearBefore.ordersTotal, 2)}`}</CellPrimary>
                <CellPrimary className="bordered">{`${formatCurrency(accomplishedData.projection.ordersTotal, 2)}`}</CellPrimary>
                <CellNumber className="bordered">{`${formatNumber(accomplishedData.yearVariation.ordersTotal, 2)}%`}</CellNumber>
              </TableRow>
              <TableRow className={classes.rowHoverFixed}>
                <StyledTableCell>QTD Pedidos</StyledTableCell>
                <StyledTableCell className="blue">{formatNumber(goalMonth.ordersQuantity)}</StyledTableCell>
                <StyledTableCell className="blue">{formatNumber(goalPartial.ordersQuantity)}</StyledTableCell>
                <CellPrimary>{formatNumber(fulfilledInMonth.ordersQuantity)}</CellPrimary>
                <CellNumber>{formatNumber(goalDiff.ordersQuantity)}</CellNumber>
                <CellNumber>{`${formatNumber(goalDiffPercent.ordersQuantity, 1)}%`}</CellNumber>
                <CellPrimary>{`${formatNumber(accomplishedData.yearBefore.ordersQuantity, 0)}`}</CellPrimary>
                <CellPrimary>{`${formatNumber(accomplishedData.projection.ordersQuantity, 0)}`}</CellPrimary>
                <CellNumber>{`${formatNumber(accomplishedData.yearVariation.ordersQuantity, 2)}%`}</CellNumber>
              </TableRow>
              <TableRow className={classes.rowHoverFixed}>
                <StyledTableCell>QTD Ped por Loja</StyledTableCell>
                <StyledTableCell colSpan={2} align="center" className="blue">{formatNumber(goalMonth.ordersByPharmaciesAverage)}</StyledTableCell>
                <CellPrimary>{formatNumber(fulfilledInMonth.ordersByPharmaciesAverage, 2)}</CellPrimary>
                <CellNumber>{formatNumber(goalDiff.ordersByPharmaciesAverage, 2)}</CellNumber>
                <CellNumber>{`${formatNumber(goalDiffPercent.ordersByPharmaciesAverage, 1)}%`}</CellNumber>
                <CellPrimary>{`${formatNumber(accomplishedData.yearBefore.ordersByPharmaciesAverage, 2)}`}</CellPrimary>
                <CellPrimary>{`${formatNumber(accomplishedData.projection.ordersByPharmaciesAverage, 2)}`}</CellPrimary>
                <CellNumber>{`${formatNumber(accomplishedData.yearVariation.ordersByPharmaciesAverage, 2)}%`}</CellNumber>
              </TableRow>
              <TableRow className={classes.rowHoverFixed}>
                <StyledTableCell className="bordered">R$ Ticket Médio - Pedido</StyledTableCell>
                <StyledTableCell colSpan={2} align="center" className="blue bordered">{formatCurrency(goalMonth.ordersAverage, 2)}</StyledTableCell>
                <CellPrimary className="bordered">{formatCurrency(fulfilledInMonth.ordersAverage, 2)}</CellPrimary>
                <CellNumber className="bordered">{formatCurrency(goalDiff.ordersAverage, 2)}</CellNumber>
                <CellNumber className="bordered">{`${formatNumber(goalDiffPercent.ordersAverage, 1)}%`}</CellNumber>
                <CellPrimary className="bordered">{formatCurrency(accomplishedData.yearBefore.ordersAverage, 2)}</CellPrimary>
                <CellPrimary className="bordered">{formatCurrency(accomplishedData.projection.ordersAverage, 2)}</CellPrimary>
                <CellNumber className="bordered">{`${formatNumber(accomplishedData.yearVariation.ordersAverage, 2)}%`}</CellNumber>
              </TableRow>
              {/* <TableRow className={classes.rowHoverFixed}>
                <StyledTableCell>QTD Credenc.</StyledTableCell>
                <StyledTableCell className="bold blue">{formatNumber(goalMonth.credentialsQuantity)}</StyledTableCell>
                <StyledTableCell className="bold blue">{formatNumber(goalPartial.credentialsQuantity)}</StyledTableCell>
                <CellPrimary>{formatNumber(fulfilledInMonth.credentialsQuantity, 0)}</CellPrimary>
                <CellNumber>{formatNumber(goalDiff.credentialsQuantity)}</CellNumber>
                <CellNumber>{`${formatNumber(goalDiffPercent.credentialsQuantity, 1)}%`}</CellNumber>
                <CellPrimary>{formatNumber(accomplishedData.yearBefore.credentialsQuantity, 0)}</CellPrimary>
                <CellPrimary>{formatNumber(accomplishedData.projection.credentialsQuantity, 0)}</CellPrimary>
                <CellNumber>{`${formatNumber(accomplishedData.yearVariation.credentialsQuantity, 2)}%`}</CellNumber>
              </TableRow> */}
              <TableRow className={classes.rowHoverFixed}>
                <StyledTableCell>QTD Ativ. Novas</StyledTableCell>
                <StyledTableCell className="bold blue">{formatNumber(goalMonth.activationsQuantity)}</StyledTableCell>
                <StyledTableCell className="bold blue">{formatNumber(goalPartial.activationsQuantity)}</StyledTableCell>
                <CellPrimary>{`${fulfilledInMonth.activationsQuantity}`}</CellPrimary>
                <CellNumber>{formatNumber(goalDiff.activationsQuantity)}</CellNumber>
                <CellNumber>{`${formatNumber(goalDiffPercent.activationsQuantity, 1)}%`}</CellNumber>
                <StyledTableCell>{`${formatNumber(accomplishedData.yearBefore.activationsQuantity, 0)}`}</StyledTableCell>
                <StyledTableCell>{`${formatNumber(accomplishedData.projection.activationsQuantity, 0)}`}</StyledTableCell>
                <CellNumber>{`${formatNumber(accomplishedData.yearVariation.activationsQuantity, 2)}%`}</CellNumber>
              </TableRow>
              <TableRow className={classes.rowHoverFixed}>
                <StyledTableCell className="bordered">QTD Lojas c/ VD</StyledTableCell>
                <StyledTableCell className="blue bold bordered" colSpan={2}>{formatNumber(goalMonth.pharmaciesQuantity)}</StyledTableCell>
                <CellPrimary className="bordered">{formatNumber(fulfilledInMonth.pharmaciesQuantity)}</CellPrimary>
                <CellNumber className="bordered">{formatNumber(goalDiff.pharmaciesQuantity)}</CellNumber>
                <CellNumber className="bordered">{`${formatNumber(goalDiffPercent.pharmaciesQuantity, 1)}%`}</CellNumber>
                <CellPrimary className="bordered">{formatNumber(accomplishedData.yearBefore.pharmaciesQuantity)}</CellPrimary>
                <CellPrimary className="bordered">{formatNumber(accomplishedData.projection.pharmaciesQuantity)}</CellPrimary>
                <CellNumber className="bordered">{`${formatNumber(accomplishedData.yearVariation.pharmaciesQuantity, 2)}%`}</CellNumber>
              </TableRow>
              <TableRow className={classes.rowHoverFixed}>
                <StyledTableCell>QTD Usuários Md Dia</StyledTableCell>
                <StyledTableCell className="blue" colSpan={2}>{formatNumber(goalMonth.customersByDayAverage)}</StyledTableCell>
                <CellPrimary>{formatNumber(fulfilledInMonth.customersByDayAverage)}</CellPrimary>
                <CellNumber>{formatNumber(goalDiff.customersByDayAverage)}</CellNumber>
                <CellNumber>{`${formatNumber(goalDiffPercent.customersByDayAverage, 1)}%`}</CellNumber>
              </TableRow>
              <TableRow className={classes.rowHoverFixed}>
                <StyledTableCell>QTD Usuários Ac. Mês</StyledTableCell>
                <StyledTableCell className="blue" colSpan={2}>{formatNumber(goalMonth.customersQuantity)}</StyledTableCell>
                <CellPrimary>{formatNumber(fulfilledInMonth.customersQuantity)}</CellPrimary>
                <CellNumber>{formatNumber(goalDiff.customersQuantity)}</CellNumber>
                <CellNumber>{`${formatNumber(goalDiffPercent.customersQuantity, 1)}%`}</CellNumber>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  )
}
